import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { ArrowLeft, FileDown } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props extends Omit<AppShellProps, 'children'> {
  event: { id: number; name: string }
  reportType: string
  statistics: Record<string, Record<string, number>>
  usersData: Record<string, string>[]
  selectedFields: string[]
  urls: AppShellProps['urls'] & {
    prepare: string
    exportPdf: string
    exportXlsx: string
    dashboard: string
  }
  translations: AppShellProps['translations'] & {
    report: string
    statistics: string
    backToPrepare: string
    exportPdf: string
    exportXlsx: string
    name: string
    phoneNumber: string
    email: string
    organization: string
    position: string
    gender: string
    country: string
    city: string
    age: string
    idNumber: string
    passportNumber: string
    bloodType: string
    attended: string
    noData: string
  }
}

const FIELD_LABEL_MAP: Record<string, string> = {
  name: 'name',
  phone_number: 'phoneNumber',
  email: 'email',
  organization: 'organization',
  position: 'position',
  gender: 'gender',
  country: 'country',
  city: 'city',
  age: 'age',
  id_number: 'idNumber',
  passport_number: 'passportNumber',
  blood_type: 'bloodType',
  attended: 'attended',
}

/* ---------- Chart colors ---------- */
const CHART_COLORS = [
  'rgba(255, 99, 132, 0.7)',
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 206, 86, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 159, 64, 0.7)',
  'rgba(99, 255, 132, 0.7)',
  'rgba(162, 54, 235, 0.7)',
  'rgba(206, 255, 86, 0.7)',
  'rgba(192, 75, 192, 0.7)',
]

/* ------------------------------------------------------------------ */
/*  PieChart — simple SVG pie                                          */
/* ------------------------------------------------------------------ */

function PieChart({ data, title }: { data: Record<string, number>; title: string }) {
  const entries = Object.entries(data)
  const total = entries.reduce((s, [, v]) => s + v, 0)
  if (total === 0) return null

  // Calculate arcs
  let cumAngle = 0
  const arcs = entries.map(([label, value], i) => {
    const angle = (value / total) * 360
    const startAngle = cumAngle
    cumAngle += angle
    return { label, value, angle, startAngle, color: CHART_COLORS[i % CHART_COLORS.length] }
  })

  function polarToCartesian(cx: number, cy: number, r: number, degrees: number) {
    const rad = ((degrees - 90) * Math.PI) / 180
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
  }

  function arcPath(cx: number, cy: number, r: number, start: number, end: number) {
    const startP = polarToCartesian(cx, cy, r, start)
    const endP = polarToCartesian(cx, cy, r, end)
    const largeArc = end - start > 180 ? 1 : 0
    return `M ${cx} ${cy} L ${startP.x} ${startP.y} A ${r} ${r} 0 ${largeArc} 1 ${endP.x} ${endP.y} Z`
  }

  return (
    <div className="rounded-lg border border-[#E8E8E5] p-4">
      <h3 className="mb-3 text-center text-sm font-semibold text-[#37352F]">{title}</h3>
      <svg viewBox="0 0 200 200" className="mx-auto" style={{ maxWidth: 200 }}>
        {arcs.map((arc, i) =>
          arc.angle >= 360 ? (
            <circle key={i} cx={100} cy={100} r={80} fill={arc.color} />
          ) : (
            <path key={i} d={arcPath(100, 100, 80, arc.startAngle, arc.startAngle + arc.angle)} fill={arc.color} />
          ),
        )}
      </svg>
      <div className="mt-3 space-y-1">
        {arcs.map((arc, i) => (
          <div key={i} className="flex items-center gap-2 text-xs text-[#37352F]">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: arc.color }} />
            <span className="flex-1">{arc.label}</span>
            <span className="font-medium">{arc.value} ({Math.round((arc.value / total) * 100)}%)</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Create(props: Props) {
  const {
    event,
    reportType,
    statistics,
    usersData,
    selectedFields,
    urls,
    translations: t,
    ...shell
  } = props

  const isStatistics = Object.keys(statistics).length > 0

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={`${t.report} — ${event.name}`} />

      <div className="mx-auto max-w-6xl py-8 px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => router.visit(urls.prepare)}
              className="text-[#91918E] hover:bg-[#F1F1EF]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-[#37352F]">{t.report}</h1>
              <p className="text-sm text-[#91918E]">{event.name} — {reportType}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <a href={urls.exportPdf} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <FileDown className="mr-1 h-4 w-4" />
                {t.exportPdf}
              </Button>
            </a>
            <a href={urls.exportXlsx} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <FileDown className="mr-1 h-4 w-4" />
                {t.exportXlsx}
              </Button>
            </a>
          </div>
        </div>

        {/* Statistics view */}
        {isStatistics && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {Object.entries(statistics).map(([field, data]) => (
              <PieChart
                key={field}
                data={data}
                title={t[FIELD_LABEL_MAP[field] as keyof typeof t] as string || field}
              />
            ))}
          </div>
        )}

        {/* General / attendance table */}
        {!isStatistics && usersData.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-[#E8E8E5]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F1F1EF]">
                  {selectedFields.map((f) => (
                    <th key={f} className="px-4 py-3 text-left font-medium text-[#91918E]">
                      {t[FIELD_LABEL_MAP[f] as keyof typeof t] as string || f}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usersData.map((row, i) => (
                  <tr key={i} className="border-t border-[#E8E8E5] transition-colors hover:bg-[#F1F1EF]">
                    {selectedFields.map((f) => (
                      <td key={f} className="px-4 py-3 text-[#37352F]">
                        {row[f] || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* No data */}
        {!isStatistics && usersData.length === 0 && (
          <div className="rounded-lg border border-[#E8E8E5] p-8 text-center text-[#91918E]">
            {t.noData}
          </div>
        )}
      </div>
    </AppShell>
  )
}
